(() => {
  const namespace = '__guruVideoProductGridV1';
  const markerSelector = 'script[data-guru-video-product-grid-root]';
  if (window[namespace]) {
    window[namespace].scan(document);
    return;
  }

  const initializedRoots = new WeakSet();
  const cleanupByRoot = new WeakMap();

  function bindGrid(grid, signal, cleanups) {
    const track = grid.querySelector('[data-guru-video-product-grid-track]');
    const controls = grid.querySelector('[data-guru-video-product-grid-controls]');
    const previous = grid.querySelector('[data-guru-video-product-grid-previous]');
    const next = grid.querySelector('[data-guru-video-product-grid-next]');
    if (!track || !controls || !previous || !next) return false;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let animationFrame = 0;

    const playbackToggles = [...grid.querySelectorAll('[data-guru-video-product-playback-toggle]')];
    playbackToggles.forEach((button) => {
      const shell = button.closest('[data-guru-media-shell]');
      const video = shell?.querySelector('[data-guru-deferred-video]');
      if (!video) return;
      const audioButton = shell.querySelector('[data-guru-video-product-audio-toggle]');

      video.defaultMuted = true;
      video.muted = true;
      video.setAttribute('muted', '');

      const syncCardControls = () => {
        const playing = shell.classList.contains('is-playing');
        button.dataset.playbackState = playing ? 'playing' : 'paused';
        button.setAttribute('aria-pressed', playing ? 'true' : 'false');
        button.setAttribute('aria-label', playing ? button.dataset.pauseLabel : button.dataset.playLabel);
        if (!audioButton) return;
        const muted = video.muted;
        audioButton.hidden = !playing;
        audioButton.dataset.audioState = muted ? 'muted' : 'unmuted';
        audioButton.setAttribute('aria-pressed', muted ? 'false' : 'true');
        audioButton.setAttribute('aria-label', muted ? audioButton.dataset.unmuteLabel : audioButton.dataset.muteLabel);
      };

      button.addEventListener('click', () => {
        grid.querySelectorAll('[data-guru-deferred-video]').forEach((candidate) => {
          if (candidate !== video && candidate.closest('[data-guru-media-shell]')?.classList.contains('is-playing')) {
            candidate.dispatchEvent(new CustomEvent('guru:media-toggle', { bubbles: true, detail: { video: candidate } }));
          }
        });
        video.dispatchEvent(new CustomEvent('guru:media-toggle', { bubbles: true, detail: { video } }));
      }, { signal });
      audioButton?.addEventListener('click', () => {
        video.muted = !video.muted;
        if (!video.muted && video.volume === 0) video.volume = 1;
        syncCardControls();
      }, { signal });
      ['play', 'pause', 'ended', 'error', 'volumechange', 'guru:media-statechange'].forEach((eventName) => {
        video.addEventListener(eventName, syncCardControls, { signal });
      });
      syncCardControls();
    });

    const update = () => {
      animationFrame = 0;
      if (!track.isConnected) return;
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const overflow = maxScroll > 1;
      controls.hidden = !overflow;
      previous.disabled = !overflow || track.scrollLeft <= 2;
      next.disabled = !overflow || track.scrollLeft >= maxScroll - 2;
    };

    const queueUpdate = () => {
      if (!animationFrame) animationFrame = window.requestAnimationFrame(update);
    };

    const cardStep = () => {
      const card = track.querySelector('[data-guru-video-product-card]');
      if (!card) return Math.max(1, track.clientWidth);
      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      return Math.max(1, card.getBoundingClientRect().width + gap);
    };

    const move = (direction) => {
      track.scrollBy({
        left: direction * cardStep(),
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    };

    previous.addEventListener('click', () => move(-1), { signal });
    next.addEventListener('click', () => move(1), { signal });
    track.addEventListener('scroll', queueUpdate, { passive: true, signal });
    window.addEventListener('resize', queueUpdate, { passive: true, signal });
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(queueUpdate) : null;
    resizeObserver?.observe(track);
    queueUpdate();

    cleanups.push(() => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      controls.hidden = true;
      previous.disabled = true;
      next.disabled = true;
    });
    return true;
  }

  function initRoot(sectionRoot) {
    if (!sectionRoot || initializedRoots.has(sectionRoot)) return false;
    const abortController = new AbortController();
    const cleanups = [];
    const grids = [...sectionRoot.querySelectorAll('[data-guru-video-product-grid]')];
    const bound = grids.map((grid) => bindGrid(grid, abortController.signal, cleanups)).some(Boolean);
    if (!bound) {
      abortController.abort();
      return false;
    }

    sectionRoot.dataset.guruVideoProductGridBound = 'true';
    initializedRoots.add(sectionRoot);
    cleanupByRoot.set(sectionRoot, () => {
      abortController.abort();
      cleanups.forEach((cleanup) => cleanup());
      delete sectionRoot.dataset.guruVideoProductGridBound;
      initializedRoots.delete(sectionRoot);
      cleanupByRoot.delete(sectionRoot);
    });
    return true;
  }

  function cleanupRoot(sectionRoot) {
    const cleanup = cleanupByRoot.get(sectionRoot);
    if (!cleanup) return false;
    cleanup();
    return true;
  }

  function scan(scope = document) {
    const markers = [];
    if (scope.matches?.(markerSelector)) markers.push(scope);
    markers.push(...(scope.querySelectorAll?.(markerSelector) || []));
    markers.forEach((marker) => initRoot(document.getElementById(marker.dataset.guruVideoProductGridRoot)));
  }

  document.addEventListener('shopify:section:load', (event) => {
    initRoot(event.target);
    scan(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => cleanupRoot(event.target));

  const observer = new MutationObserver((records) => {
    records.forEach((record) => record.removedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;
      if (node.matches('[data-guru-video-product-grid-bound]')) cleanupRoot(node);
      node.querySelectorAll?.('[data-guru-video-product-grid-bound]').forEach(cleanupRoot);
    }));
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node instanceof Element) scan(node);
    }));
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
  window[namespace] = { cleanupRoot, initRoot, scan };
  scan(document);
})();
